import React, { useState, useEffect } from 'react';
import { X, Copy, Crown, Sparkles, Users, Shield, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import { generateParties, formatPartiesForCopy, getPartiesStats, isBuffClass, formatListForCopy } from '../../utils/partyGenerator';
import { getClassTypeIcon } from "../../utils/characterTranslation";

const PartyGeneratorModal = ({ isOpen, onClose, event, participations }) => {
  const [parties, setParties] = useState([]);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedList, setCopiedList] = useState(false);


  useEffect(() => {
    if (isOpen && participations) {
      const generatedParties = generateParties(participations);
      setParties(generatedParties);
      setStats(getPartiesStats(generatedParties));
    }
  }, [isOpen, participations]);

  const handleCopy = () => {
    const formattedText = formatPartiesForCopy(parties, event?.name || 'Evento');
    navigator.clipboard.writeText(formattedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyList = () => {
    const formattedText = formatListForCopy(participations, event?.name || 'Evento');
    navigator.clipboard.writeText(formattedText);
    setCopiedList(true);
    setTimeout(() => setCopiedList(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto border border-purple-500/20">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="w-7 h-7" />
                Divisão Automática de Grupos
              </h2>
              <p className="text-purple-100 mt-1">
                {event?.name} - {event?.type}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="p-6 bg-slate-900/50 border-b border-white/10">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-400">{stats.totalParties}</div>
                <div className="text-sm text-gray-300 mt-1">Grupos</div>
              </div>
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-400">{stats.totalMembers}</div>
                <div className="text-sm text-gray-300 mt-1">Membros</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-400">{stats.totalBuffs}</div>
                <div className="text-sm text-gray-300 mt-1">Buffs</div>
              </div>
              <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-400">{stats.averagePartySize}</div>
                <div className="text-sm text-gray-300 mt-1">Média/Grupo</div>
              </div>
              <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/30 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-400">{stats.partiesWithoutBuff}</div>
                <div className="text-sm text-gray-300 mt-1">Sem Buff</div>
              </div>
            </div>

            {stats.partiesWithoutBuff > 0 && (
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-300">
                  <strong>Atenção:</strong> {stats.partiesWithoutBuff} {stats.partiesWithoutBuff === 1 ? 'grupo está' : 'grupos estão'} sem Buff.
                  Considere ajustar manualmente para melhor balanceamento.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Parties */}
        <div className="p-6 space-y-6">
          {parties.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                Nenhum participante confirmado para gerar grupos.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {parties.map((party) => (
                <div
                  key={party.id}
                  className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/10 rounded-xl p-5 hover:border-purple-500/50 transition-all duration-300"
                >
                  {/* Party Header */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Shield className="w-5 h-5 text-purple-400" />
                        Party {party.id}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {party.members.length} membros • Lvl médio: {party.averageLevel}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {party.buffCount > 0 && (
                        <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                          <Sparkles className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-semibold text-purple-300">
                            {party.buffCount} {party.buffCount === 1 ? 'Buff' : 'Buffs'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Party Members */}
                  <div className="space-y-2">
                    {party.members.map((member, index) => {
                      const isLeader = member.id === party.leader?.id;
                      const isBuff = isBuffClass(member.character?.class);

                      return (
                        <div
                          key={member.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${isLeader
                            ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/50'
                            : 'bg-white/5 hover:bg-white/10'
                            }`}
                        >
                          {/* Position Number */}
                          <div className="flex-shrink-0 w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center text-white font-bold">
                            {index + 1}
                          </div>

                          {/* Character Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-base font-semibold text-white truncate">
                                {member.character?.name} {member.pilot ? `(${member.pilot})` : ''}
                              </span>

                              {isLeader && (
                                <div className="flex items-center gap-1 bg-yellow-500/20 border border-yellow-500/40 rounded px-2 py-0.5">
                                  <Crown className="w-3 h-3 text-yellow-400" />
                                  <span className="text-xs font-bold text-yellow-300">LÍDER</span>
                                </div>
                              )}

                              {isBuff && !isLeader && (
                                <Sparkles className="w-4 h-4 text-purple-400" title="Buff" />
                              )}
                            </div>

                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                              <span>{getClassTypeIcon(member.character?.class)}</span>
                              <span>{member.character?.class}</span>
                              <span className="text-gray-500">•</span>
                              <span>Lvl {member.character?.level}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 p-6 border-t border-white/10 rounded-b-2xl flex justify-between items-center gap-4">
          <div className="text-sm text-gray-400">
            💡 <strong>Dica:</strong> Líderes são destacados com {' '}
            <Crown className="w-4 h-4 inline text-yellow-400" /> e buffs com {' '}
            <Sparkles className="w-4 h-4 inline text-purple-400" />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Fechar
            </Button>
            {parties.length > 0 && (
              <div className="flex gap-3">
                <Button
                  onClick={handleCopyList}
                  className="flex items-center gap-2"
                >
                  <Copy size={18} />
                  {copiedList ? 'Copiado!' : 'Copiar lista'}
                </Button>
                <Button
                  onClick={handleCopy}
                  className="flex items-center gap-2"
                >
                  <Copy size={18} />
                  {copied ? 'Copiado!' : 'Copiar Grupos'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartyGeneratorModal;
